import React, { useState, useMemo } from "react";
import styles from "../styles/BuiltUpAreaCalculator.module.css";
import { useAppContext } from "../context";

const PlotAreaCalculator = ({ onNext }) => {
  const [grossArea, setGrossArea] = useState("");
  const [roadArea, setRoadArea] = useState("");
  const [reserveArea, setReserveArea] = useState("");
  const [areaType, setAreaType] = useState("");
  const [authority, setAuthority] = useState("");
  const [roadWidth, setRoadWidth] = useState("");
  const { setPlotData } = useAppContext();

  // -----------------------------
  // RAW DATA TABLE (Sample Reference)
  // -----------------------------
  const rawData = [
    // ----- Congested + CIDCO/MC -----
    { areaType: "Congested", authority: "CIDCO/MC", roadWidth: "Below 9 m", basicFSI: 1.50, premiumFSI: 0.00, maxTDR: 0.00, tdrFactor: 3 },
    { areaType: "Congested", authority: "CIDCO/MC", roadWidth: "9 m and below 18 m", basicFSI: 2.00, premiumFSI: 0.30, maxTDR: 0.30, tdrFactor: 3 },
    { areaType: "Congested", authority: "CIDCO/MC", roadWidth: "18 m and below 30 m", basicFSI: 2.00, premiumFSI: 0.30, maxTDR: 0.50, tdrFactor: 3 },
    { areaType: "Congested", authority: "CIDCO/MC", roadWidth: "30 m and above", basicFSI: 2.00, premiumFSI: 0.30, maxTDR: 0.70, tdrFactor: 3 },

    // ----- Congested + Other Areas -----
    { areaType: "Congested", authority: "Other", roadWidth: "Below 9 m", basicFSI: 1.50, premiumFSI: 0.00, maxTDR: 0.00, tdrFactor: 3},
    { areaType: "Congested", authority: "Other", roadWidth: "9 m and below 18 m", basicFSI: 2.00, premiumFSI: 0.30, maxTDR: 0.10, tdrFactor: 3},
    { areaType: "Congested", authority: "Other", roadWidth: "18 m and below 30 m", basicFSI: 2.00, premiumFSI: 0.30, maxTDR: 0.20, tdrFactor: 3},
    { areaType: "Congested", authority: "Other", roadWidth: "30 m and above", basicFSI: 2.00, premiumFSI: 0.30, maxTDR: 0.20, tdrFactor: 3},

    // ----- Non-Congested + CIDCO/MC -----
    { areaType: "Non-Congested", authority: "CIDCO/MC", roadWidth: "Below 9 m", basicFSI: 1.10, premiumFSI: 0.00, maxTDR: 0.00, tdrFactor: 2 },
    { areaType: "Non-Congested", authority: "CIDCO/MC", roadWidth: "9 m and below 12 m", basicFSI: 1.10, premiumFSI: 0.50, maxTDR: 0.40, tdrFactor: 2 },
    { areaType: "Non-Congested", authority: "CIDCO/MC", roadWidth: "12 m and below 15 m", basicFSI: 1.10, premiumFSI: 0.50, maxTDR: 0.65, tdrFactor: 2 },
    { areaType: "Non-Congested", authority: "CIDCO/MC", roadWidth: "15 m and below 24 m", basicFSI: 1.10, premiumFSI: 0.50, maxTDR: 0.90, tdrFactor: 2 },
    { areaType: "Non-Congested", authority: "CIDCO/MC", roadWidth: "24 m and below 30 m", basicFSI: 1.10, premiumFSI: 0.50, maxTDR: 1.15, tdrFactor: 2 },
    { areaType: "Non-Congested", authority: "CIDCO/MC", roadWidth: "30 m and above", basicFSI: 1.10, premiumFSI: 0.50, maxTDR: 1.40, tdrFactor: 2 },

    // ----- Non-Congested + Other Areas -----
    { areaType: "Non-Congested", authority: "Other", roadWidth: "Below 9 m", basicFSI: 1.10, premiumFSI: 0.00, maxTDR: 0.00, tdrFactor: 2 },
    { areaType: "Non-Congested", authority: "Other", roadWidth: "9 m and below 12 m", basicFSI: 1.10, premiumFSI: 0.30, maxTDR: 0.30, tdrFactor: 2 },
    { areaType: "Non-Congested", authority: "Other", roadWidth: "12 m and below 15 m", basicFSI: 1.10, premiumFSI: 0.30, maxTDR: 0.60, tdrFactor: 2 },
    { areaType: "Non-Congested", authority: "Other", roadWidth: "15 m and below 24 m", basicFSI: 1.10, premiumFSI: 0.30, maxTDR: 0.70, tdrFactor: 2 },
    { areaType: "Non-Congested", authority: "Other", roadWidth: "24 m and below 30 m", basicFSI: 1.10, premiumFSI: 0.30, maxTDR: 0.90, tdrFactor: 2 },
    { areaType: "Non-Congested", authority: "Other", roadWidth: "30 m and above", basicFSI: 1.10, premiumFSI: 0.30, maxTDR: 1.10, tdrFactor: 2 },
  ];

  // -----------------------------
  // Calculations
  // -----------------------------
  const totalReservation =
    parseFloat(roadArea || 0) + parseFloat(reserveArea || 0);
  const netPlotArea = parseFloat(grossArea || 0) - totalReservation;

  // -----------------------------
  // Dynamic Dropdown for Road Width
  // -----------------------------
  const roadWidthOptions = useMemo(() => {
    if (!areaType || !authority) return [];

    // Congested + CIDCO/MC
    if (areaType === "Congested" && authority === "CIDCO/MC")
      return [
        "Below 9 m",
        "9 m and below 18 m",
        "18 m and below 30 m",
        "30 m and above",
      ];

    // Congested + Other Areas
    if (areaType === "Congested" && authority === "Other")
      return [
        "Below 9 m",
        "9 m and below 18 m",
        "18 m and below 30 m",
        "30 m and above",
      ];

    // Non-Congested + CIDCO/MC
    if (areaType === "Non-Congested" && authority === "CIDCO/MC")
      return [
        "Below 9 m",
        "9 m and below 12 m",
        "12 m and below 15 m",
        "15 m and below 24 m",
        "24 m and below 30 m",
        "30 m and above",
      ];

    // Non-Congested + Other Areas
    if (areaType === "Non-Congested" && authority === "Other")
      return [
        "Below 9 m",
        "9 m and below 12 m",
        "12 m and below 15 m",
        "15 m and below 24 m",
        "24 m and below 30 m",
        "30 m and above",
      ];

    return [];
  }, [areaType, authority]);

  // -----------------------------
  // Fetch Output Data
  // -----------------------------
  const selectedData = rawData.find(
    (item) =>
      item.areaType === areaType &&
      item.authority === authority &&
      item.roadWidth === roadWidth
  );

  const basicFSI = selectedData?.basicFSI ?? 0;
  const premiumFSI = selectedData?.premiumFSI ?? 0;
  const maxTDR = selectedData?.maxTDR ?? 0;
  const tdrFactor = selectedData?.tdrFactor ?? 0;

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <div className={styles.builtUpArea_sectionContainer}>
      <h3 className={styles.builtUpArea_sectionTitle}>Plot Area Calculator</h3>

      {/* ---- Input Fields ---- */}
      <div className={styles.builtUpArea_inputGroup}>
        <label>Gross Plot Area (Sqm):</label>
        <input
          type="number"
          value={grossArea}
          onChange={(e) => setGrossArea(e.target.value)}
          placeholder="Enter Value"
        />
      </div>

      <div className={styles.builtUpArea_inputGroup}>
        <label>D.P. Road Area (Sqm):</label>
        <input
          type="number"
          value={roadArea}
          onChange={(e) => setRoadArea(e.target.value)}
          placeholder="Enter Value"
        />
      </div>

      <div className={styles.builtUpArea_inputGroup}>
        <label>Other Reservation Area (Sqm):</label>
        <input
          type="number"
          value={reserveArea}
          onChange={(e) => setReserveArea(e.target.value)}
          placeholder="Enter Value"
        />
      </div>

      {/* ---- Read-only Calculations ---- */}
      <div className={styles.builtUpArea_readOnlyFields}>
        <div className={styles.builtUpArea_readOnlyRow}>
          <span>Total Reservation Area (Sqm):</span>
          <span>{totalReservation.toFixed(2)}</span>
        </div>
        <div className={styles.builtUpArea_readOnlyRow}>
          <span>Net Plot Area (Sqm):</span>
          <span>{netPlotArea.toFixed(2)}</span>
        </div>
      </div>

      {/* ---- First set of selects ---- */}
      <div className={styles.builtUpArea_selectGroup}>
        <label>Area Type:</label>
        <select
          value={areaType}
          onChange={(e) => {
            setAreaType(e.target.value);
            setRoadWidth("");
          }}
        >
          <option value="">Select</option>
          <option value="Congested">Congested</option>
          <option value="Non-Congested">Non-Congested</option>
        </select>
      </div>

      <div className={styles.builtUpArea_selectGroup}>
        <label>Authority Type:</label>
        <select
          value={authority}
          onChange={(e) => {
            setAuthority(e.target.value);
            setRoadWidth("");
          }}
        >
          <option value="">Select</option>
          <option value="CIDCO/MC">CIDCO/ Municipal Corporation</option>
          <option value="Other">Other Areas</option>
        </select>
      </div>

      <div className={styles.builtUpArea_selectGroup}>
        <label>Road Front Width:</label>
        <select
          value={roadWidth}
          onChange={(e) => setRoadWidth(e.target.value)}
          disabled={roadWidthOptions.length === 0}
        >
          <option value="">Select</option>
          {roadWidthOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      {/* ---- Remaining Read-only FSI Fields ---- */}
      <div className={styles.builtUpArea_readOnlyFields}>
        <div className={styles.builtUpArea_readOnlyRow}>
          <span>Basic FSI Factor:</span>
          <span>{basicFSI}</span>
        </div>
        <div className={styles.builtUpArea_readOnlyRow}>
          <span>Premium FSI Factor:</span>
          <span>{premiumFSI}</span>
        </div>
        <div className={styles.builtUpArea_readOnlyRow}>
          <span>Max TDR Loading Factor:</span>
          <span>{maxTDR}</span>
        </div>
        <div className={styles.builtUpArea_readOnlyRow}>
          <span>TDR Factor:</span>
          <span>{tdrFactor}</span>
        </div>
      </div>

      {/* ---- Next Button ---- */}
      <button
        className={styles.builtUpArea_nextButton}
        onClick={() => {
          // Save to context before moving
          setPlotData({
            district: "", // optional input fields if you have them
            taluka: "",
            village: "",
            surveyNumber: "",
            grossPlotArea: parseFloat(grossArea || 0),
            dpRoadArea: parseFloat(roadArea || 0),
            otherReserveArea: parseFloat(reserveArea || 0),
            totalReservation: totalReservation,
            netPlotArea: netPlotArea,
            areaType,
            authorityType: authority,
            roadWidth,
            basicFSIFactor: basicFSI,
            premiumFSIFactor: premiumFSI,
            maxTDRFactor: maxTDR,
            tdrFactor: areaType === "Congested" ? 3 : 2,
          });
          onNext(); // navigate to Screen 2
        }}
      >
        NEXT
      </button>
    </div>
  );
};

export default PlotAreaCalculator;
